import { useState } from "react"
import {
  Alert,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material"
import { Form } from "@core/components/form/form"
import {
  useOrderFiltersListQuery,
  useTypesListQuery,
} from "@/modules/adv2/store/advApi"
import type {
  Binding,
  NamedId,
  ReadBindings,
} from "../../interface/publications"
import { bindingCatalogs } from "../../services/adv/catalogs"
import { bindingUrl, isBinding, toBindingWrite } from "../../utils/bindings"
import { CatalogSelect } from "./catalogSelect"

export interface BindingValueFormProps {
  value?: ReadBindings[string]
  /** Full replacement for ONE binding in the local draft; never a business write. */
  onApply: (value: Binding) => void
  onCancel?: () => void
  disabled?: boolean
}

export function BindingValueForm(props: BindingValueFormProps) {
  // A new saved value resets the editor; catalog refreshes never do.
  return <BindingValueEditor key={JSON.stringify(props.value)} {...props} />
}

function BindingValueEditor({
  value,
  onApply,
  onCancel,
  disabled,
}: BindingValueFormProps) {
  const filters = useOrderFiltersListQuery({})
  const types = useTypesListQuery({ queryParams: { table: "bots" } })
  const catalog = bindingCatalogs(
    filters.error ? undefined : filters.currentData,
    types.error ? undefined : types.currentData,
  )
  const [mode, setMode] = useState(
    value?.botId !== undefined ? "legacy" : value?.bot ? "bot" : "url",
  )
  const [url, setUrl] = useState(value?.url ?? "")
  const [bot, setBot] = useState<NamedId | null>(value?.bot ?? null)
  const [botType, setBotType] = useState<NamedId | null>(value?.botType ?? null)
  const [referral, setReferral] = useState<NamedId | null>(
    value?.referral ?? null,
  )
  const replacement =
    mode === "url"
      ? { url }
      : mode === "bot"
        ? {
            bot,
            ...(botType ? { botType } : {}),
            ...(referral ? { referral } : {}),
          }
        : undefined
  const valid = isBinding(replacement)
  return (
    <Form
      onSubmit={() => {
        if (!disabled && isBinding(replacement))
          onApply(toBindingWrite(replacement))
      }}
      actions={[
        ...(onCancel
          ? [
              <Button key="cancel" onClick={onCancel}>
                Отмена
              </Button>,
            ]
          : []),
        <Button key="apply" type="submit" disabled={disabled || !valid}>
          Применить значение
        </Button>,
      ]}
    >
      {value?.botId !== undefined && (
        <Alert severity="warning">
          Сохранён botId={value.botId}; имя неизвестно. Выберите URL или бота
          для явного преобразования.
        </Alert>
      )}
      {(filters.error != null || types.error != null) && (
        <Alert severity="warning">
          Каталог ADV недоступен. Сохранённые значения и ручной URL остаются
          доступны.
          <Button
            onClick={() => {
              void filters.refetch()
              void types.refetch()
            }}
          >
            Повторить загрузку каталога
          </Button>
        </Alert>
      )}
      <RadioGroup
        row
        aria-label="Вид значения"
        value={mode}
        onChange={(_event, next) => setMode(next)}
      >
        <FormControlLabel
          value="url"
          control={<Radio />}
          label="URL"
          disabled={disabled}
        />
        <FormControlLabel
          value="bot"
          control={<Radio />}
          label="Бот + REF"
          disabled={disabled}
        />
      </RadioGroup>
      {mode === "url" && (
        <TextField
          size="small"
          label="HTTP(S) URL"
          value={url}
          onChange={(event) => setUrl(event.target.value)}
          disabled={disabled}
        />
      )}
      {mode === "bot" && (
        <>
          <CatalogSelect
            label="Бот"
            value={bot}
            options={catalog.bots}
            onChange={setBot}
            isFetching={filters.isFetching}
            disabled={disabled}
          />
          <CatalogSelect
            label="Тип бота (необязательно)"
            value={botType}
            options={catalog.botTypes}
            onChange={setBotType}
            isFetching={types.isFetching}
            disabled={disabled}
          />
          <CatalogSelect
            label="REF (необязательно)"
            value={referral}
            options={catalog.referrals}
            onChange={setReferral}
            isFetching={filters.isFetching}
            disabled={disabled}
          />
          {referral && (
            <Button onClick={() => setReferral(null)} disabled={disabled}>
              Очистить REF
            </Button>
          )}
        </>
      )}
      {!valid && mode !== "legacy" && (
        <Alert severity="info">
          {mode === "url" ? "Укажите HTTP(S) URL" : "Выберите бота"}
        </Alert>
      )}
      {valid && (
        <Typography variant="caption" sx={{ overflowWrap: "anywhere" }}>
          Итоговый адрес: {bindingUrl(replacement as ReadBindings[string])}
        </Typography>
      )}
    </Form>
  )
}
